// LogIn.tsx
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Link,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { getAuth } from 'firebase/auth';
import React, { useState, useEffect, useRef } from 'react';
import client from '../../apolloClient';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateUserMutation } from '../../generated/graphql';
import { GET_CURRENT_USER } from '../../utils/queries';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { IoWarningOutline, IoMailOutline, IoLockClosedOutline, IoPersonOutline, IoEyeOutline, IoEyeOffOutline, IoCopyOutline, IoCheckmarkOutline } from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import { MdOutlineSmartToy } from 'react-icons/md';
import { IoPersonCircleOutline } from 'react-icons/io5';
import { useGetPlatformStatsQuery } from '../../generated/graphql';

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
  }
  return num.toString();
};

const Login: React.FC = () => {
  const { data: statsData } = useGetPlatformStatsQuery();
  const [authMode, setAuthMode] = useState<'humans' | 'agents'>('agents');
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);

  // Check if passwords match for signup
  const passwordsMatch = password === confirmPassword;
  const passwordMismatchError =
    !isLogin && password && confirmPassword && !passwordsMatch;

  const [createUser, { loading: createUserLoading }] = useCreateUserMutation({
    update(cache, { data }) {
      if (data?.createUser) {
        cache.writeQuery({
          query: GET_CURRENT_USER,
          data: {
            user: {
              ...data.createUser,
              currentOrganization: data.createUser.currentOrganization || null,
            },
          },
        });
      }
    },
    errorPolicy: 'all',
  });

  const {
    loginWithEmail,
    signUpWithEmail,
    loginWithGoogle,
    setBackendUserData,
    loading: authLoading,
  } = useAuth();

  const isLoading = loading || authLoading || createUserLoading;

  // Password validation
  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  useEffect(() => {
    if (!isLogin && password) {
      setPasswordError(validatePassword(password));
    } else {
      setPasswordError('');
    }
  }, [password, isLogin]);

  const isFormValid = isLogin
    ? email.trim() !== '' && password.trim() !== ''
    : email.trim() !== '' &&
      password.trim() !== '' &&
      confirmPassword.trim() !== '' &&
      username.trim() !== '' &&
      passwordsMatch &&
      !passwordError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      isLoading ||
      !isFormValid ||
      (!isLogin && (!passwordsMatch || passwordError))
    ) {
      return;
    }

    setError('');
    setLoading(true);

    if (isLogin) {
      try {
        await loginWithEmail(email, password);
        const { currentUser } = getAuth();

        if (!currentUser) {
          throw new Error('No user found after login');
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
        await currentUser.getIdToken(true);

        try {
          const { data } = await client.query({
            query: GET_CURRENT_USER,
            fetchPolicy: 'network-only',
          });

          if (data?.user) {
            setBackendUserData(data.user);
            return;
          } else {
            throw new Error(
              'User not recognized. Please sign up before logging in.'
            );
          }
        } catch (queryError) {
          console.error('Error checking/creating backend user:', queryError);
          setError(
            queryError instanceof Error
              ? queryError.message
              : 'Failed to authenticate'
          );
          await client.clearStore();
          await client.resetStore();
          setLoading(false);
        }
      } catch (err) {
        console.error('Login flow error:', err);
        setError(err instanceof Error ? err.message : 'Failed to authenticate');
        setLoading(false);
      }
    } else {
      try {
        setError('');

        try {
          await signUpWithEmail(email, password);
        } catch (err: any) {
          if (err.code === 'auth/email-already-in-use') {
            setError(
              'This email is already registered. Please log in instead.'
            );
            setLoading(false);
            return;
          }
          throw err;
        }

        const { currentUser } = getAuth();

        if (!currentUser) {
          throw new Error('No user found after signup');
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
        await currentUser.getIdToken(true);

        const result = await createUser({
          variables: {
            email: currentUser.email || '',
            name: username,
            uid: currentUser.uid,
          },
        });

        if (!result.data?.createUser) {
          throw new Error('Failed to create user in backend');
        }

        // Check for invitation email match - if signing up with the same email as the invitation,
        // treat as verified (receiving the invite email proves email ownership)
        const pendingInvitationEmail = sessionStorage.getItem('pendingInvitationEmail');
        if (pendingInvitationEmail &&
            email.toLowerCase() === pendingInvitationEmail.toLowerCase()) {
          // Email matches invitation - treat as verified
          sessionStorage.setItem('invitationVerified', 'true');
          sessionStorage.removeItem('pendingInvitationEmail');
        }

        if (result.data?.createUser) {
          if (!result.data.createUser.currentOrganization) {
            try {
              const { data: fullUserData } = await client.query({
                query: GET_CURRENT_USER,
                fetchPolicy: 'network-only',
              });

              if (fullUserData?.user) {
                setBackendUserData(fullUserData.user);
              } else {
                setError('User created but organization setup required');
                setLoading(false);
              }
            } catch (fetchError) {
              console.error('Error fetching full user data:', fetchError);
              setError('Error setting up your account. Please try again.');
              setLoading(false);
            }
          } else {
            setBackendUserData(result.data.createUser);
          }
        }

        await client.clearStore();
        await client.resetStore();
      } catch (err) {
        console.error('Signup flow error:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to create account'
        );
        setLoading(false);
      }
    }
  };

  // Track whether Google OAuth popup is pending so we can reset on window focus
  const googlePopupPending = useRef(false);

  useEffect(() => {
    const handleFocus = () => {
      if (!googlePopupPending.current) return;
      // User returned to main window while popup was open — give a brief
      // grace period for the auth to complete, then reset loading state
      const timer = setTimeout(() => {
        if (googlePopupPending.current) {
          googlePopupPending.current = false;
          setLoading(false);
        }
      }, 2000);
      return () => clearTimeout(timer);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    googlePopupPending.current = true;

    try {
      await loginWithGoogle();
      googlePopupPending.current = false;
      const { currentUser } = getAuth();

      if (!currentUser) {
        throw new Error('No user found after Google login');
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await currentUser.getIdToken(true);

      try {
        const { data } = await client.query({
          query: GET_CURRENT_USER,
          fetchPolicy: 'network-only',
        });

        if (data?.user) {
          setBackendUserData(data.user);
          return;
        }

        const result = await createUser({
          variables: {
            email: currentUser.email || '',
            name: currentUser.displayName || currentUser.email || '',
            uid: currentUser.uid,
          },
        });

        if (!result.data?.createUser) {
          throw new Error('Failed to create user in backend');
        }

        if (result.data?.createUser) {
          if (!result.data.createUser.currentOrganization) {
            console.warn(
              'User created without organization, fetching full user data'
            );

            const { data: fullUserData } = await client.query({
              query: GET_CURRENT_USER,
              fetchPolicy: 'network-only',
            });

            if (fullUserData?.user) {
              setBackendUserData(fullUserData.user);
            } else {
              setError('User created but organization setup required');
              setLoading(false);
            }
          } else {
            setBackendUserData(result.data.createUser);
          }
        }

        await client.clearStore();
        await client.resetStore();
      } catch (queryError) {
        console.error('Error checking/creating backend user:', queryError);
        throw queryError;
      }
    } catch (err) {
      googlePopupPending.current = false;
      console.error('Google login flow error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to authenticate with Google'
      );
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      isFormValid &&
      !isLoading &&
      (isLogin || passwordsMatch)
    ) {
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const LoadingOverlay = () => (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(255, 255, 255, 0.9)"
      zIndex="9999"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      backdropFilter="blur(4px)"
    >
      <Spinner size="xl" color={HelivanColor.text.helivanBlue} thickness="3px" />
      <HelivanText variant="sectionHeader" colorOverride={HelivanColor.text.blueDark} mt={4}>
        {isLogin ? 'Signing in...' : 'Creating your account...'}
      </HelivanText>
    </Flex>
  );

  const inputStyles = {
    bg: 'white',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: HelivanColor.border.primary,
    _hover: { borderColor: HelivanColor.text.helivanBlue },
    _focus: { borderColor: HelivanColor.text.helivanBlue, boxShadow: `0 0 0 1px ${HelivanColor.text.helivanBlue}` },
    _placeholder: { color: HelivanColor.text.gray },
    height: '48px',
    fontSize: '15px',
  };

  return (
    <Flex
      minH="100vh"
      w="full"
      bg={HelivanColor.background.secondary}
    >
      {isLoading && <LoadingOverlay />}

      {/* Left side - Branding */}
      <Flex
        flex="1"
        display={['none', 'none', 'none', 'flex']}
        bg="linear-gradient(135deg, #D8E8FF 0%, #E8F1FF 50%, #F0F6FF 100%)"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        overflow="hidden"
      >
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-20%"
          right="-10%"
          width="400px"
          height="400px"
          borderRadius="full"
          bg="rgba(0,71,125,0.05)"
        />
        <Box
          position="absolute"
          bottom="-15%"
          left="-5%"
          width="300px"
          height="300px"
          borderRadius="full"
          bg="rgba(0,71,125,0.03)"
        />

        <VStack spacing={8} zIndex={1} px={12} textAlign="center">
          <HStack spacing={3}>
            <Image
              src="/img/agent-watch-icon.svg"
              alt="AgentWatch Logo"
              height="56px"
              width="56px"
              objectFit="contain"
            />
            <Text
              fontSize="36px"
              fontWeight={600}
              color={HelivanColor.text.blueDark}
            >
              AgentWatch
            </Text>
          </HStack>

          <VStack spacing={4} maxW="420px">
            <Text
              fontSize="24px"
              fontWeight={300}
              color={HelivanColor.text.blueDark}
              lineHeight="1.4"
            >
              Monitor your AI agents. Get notified when they change.
            </Text>
            <Text
              fontSize="15px"
              color={HelivanColor.text.blueMid}
              lineHeight="1.7"
            >
              AI agents can drift over time due to model updates, fine-tuning, or changing contexts. AgentWatch tracks your agents and alerts you when their behaviors shift—so you can maintain trust and reliability.
            </Text>
          </VStack>

          <HStack spacing={6} mt={4}>
            <VStack spacing={1}>
              <HStack spacing={2}>
                <MdOutlineSmartToy size={20} color={HelivanColor.text.helivanBlue} />
                <Text fontSize="28px" fontWeight={600} color={HelivanColor.text.blueDark}>
                  {statsData?.getPlatformStats?.totalAgents ?? '500+'}
                </Text>
              </HStack>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Agents / Personas</Text>
            </VStack>
            <Box width="1px" height="40px" bg={HelivanColor.border.primary} />
            <VStack spacing={1}>
              <Text fontSize="28px" fontWeight={600} color={HelivanColor.text.blueDark}>Hourly</Text>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Monitoring</Text>
            </VStack>
            <Box width="1px" height="40px" bg={HelivanColor.border.primary} />
            <VStack spacing={1}>
              <Text fontSize="28px" fontWeight={600} color={HelivanColor.text.blueDark}>
                {statsData?.getPlatformStats?.totalChangesDetected
                  ? formatNumber(statsData.getPlatformStats.totalChangesDetected)
                  : '0'}
              </Text>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Changes Detected</Text>
            </VStack>
          </HStack>
        </VStack>
      </Flex>

      {/* Right side - Form */}
      <Flex
        flex={['1', '1', '1', '0 0 520px']}
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
        px={[6, 8, 12]}
        pt={['6', '8', '10', '8vh']}
        pb={8}
        bg="white"
        overflowY="auto"
      >
        {/* Mobile logo */}
        <HStack spacing={2} display={['flex', 'flex', 'flex', 'none']} mb={8}>
          <Image
            src="/img/agent-watch-icon.svg"
            alt="AgentWatch Logo"
            height="32px"
            width="32px"
            objectFit="contain"
          />
          <Text
            fontSize="24px"
            fontWeight={600}
            color={HelivanColor.text.blueDark}
          >
            AgentWatch
          </Text>
        </HStack>

        {/* For Humans / For Agents toggle — outside the content stack for stable position */}
        <HStack
          width="full"
          maxWidth="380px"
          bg={HelivanColor.background.secondary}
          borderRadius="12px"
          p="4px"
          spacing={0}
          mb={6}
        >
            <Button
              flex={1}
              size="sm"
              height="40px"
              borderRadius="10px"
              bg={authMode === 'humans' ? 'white' : 'transparent'}
              color={authMode === 'humans' ? HelivanColor.text.blueDark : HelivanColor.text.blueMid}
              boxShadow={authMode === 'humans' ? 'sm' : 'none'}
              fontWeight={authMode === 'humans' ? 600 : 400}
              fontSize="14px"
              _hover={{ bg: authMode === 'humans' ? 'white' : 'gray.100' }}
              onClick={() => setAuthMode('humans')}
              transition="all 0.2s"
            >
              <HStack spacing={2}>
                <IoPersonCircleOutline size={18} />
                <Text>For Humans</Text>
              </HStack>
            </Button>
            <Button
              flex={1}
              size="sm"
              height="40px"
              borderRadius="10px"
              bg={authMode === 'agents' ? 'white' : 'transparent'}
              color={authMode === 'agents' ? HelivanColor.text.blueDark : HelivanColor.text.blueMid}
              boxShadow={authMode === 'agents' ? 'sm' : 'none'}
              fontWeight={authMode === 'agents' ? 600 : 400}
              fontSize="14px"
              _hover={{ bg: authMode === 'agents' ? 'white' : 'gray.100' }}
              onClick={() => setAuthMode('agents')}
              transition="all 0.2s"
            >
              <HStack spacing={2}>
                <MdOutlineSmartToy size={18} />
                <Text>For Agents</Text>
              </HStack>
            </Button>
        </HStack>

        <Stack
          width="full"
          maxWidth="380px"
          spacing={6}
        >
          {authMode === 'humans' ? (
            <>
              <VStack spacing={2} alignItems="flex-start">
                <Text
                  fontSize="28px"
                  fontWeight={600}
                  color={HelivanColor.text.blueDark}
                >
                  {isLogin ? 'Welcome back' : 'Create an account'}
                </Text>
                <Text
                  fontSize="15px"
                  color={HelivanColor.text.blueMid}
                >
                  {isLogin
                    ? 'Sign in to continue to AgentWatch'
                    : 'Get started with AgentWatch today'}
                </Text>
              </VStack>

              {/* Google Sign In */}
              <Button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                bg="white"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                borderRadius="10px"
                height="48px"
                width="full"
                _hover={{ bg: HelivanColor.background.secondary, borderColor: HelivanColor.text.blueMid }}
                transition="all 0.2s"
              >
                <HStack spacing={3}>
                  <FcGoogle size={20} />
                  <Text fontSize="15px" fontWeight={500} color={HelivanColor.text.blueDark}>
                    Continue with Google
                  </Text>
                </HStack>
              </Button>

              {/* Divider */}
              <HStack spacing={4}>
                <Box flex="1" height="1px" bg={HelivanColor.border.primary} />
                <Text fontSize="13px" color={HelivanColor.text.gray}>or</Text>
                <Box flex="1" height="1px" bg={HelivanColor.border.primary} />
              </HStack>

              {/* Form */}
              <Stack spacing={4} as="form" onSubmit={handleSubmit}>
                {!isLogin && (
                  <InputGroup>
                    <InputLeftElement h="48px" pointerEvents="none">
                      <IoPersonOutline size={18} color={HelivanColor.text.gray} />
                    </InputLeftElement>
                    <Input
                      {...inputStyles}
                      pl="40px"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                    />
                  </InputGroup>
                )}

                <InputGroup>
                  <InputLeftElement h="48px" pointerEvents="none">
                    <IoMailOutline size={18} color={HelivanColor.text.gray} />
                  </InputLeftElement>
                  <Input
                    {...inputStyles}
                    pl="40px"
                    placeholder="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                  />
                </InputGroup>

                <InputGroup>
                  <InputLeftElement h="48px" pointerEvents="none">
                    <IoLockClosedOutline size={18} color={HelivanColor.text.gray} />
                  </InputLeftElement>
                  <Input
                    {...inputStyles}
                    pl="40px"
                    pr="48px"
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                  />
                  <InputRightElement h="48px">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
                      variant="ghost"
                      size="sm"
                      color={HelivanColor.text.gray}
                      onClick={() => setShowPassword(!showPassword)}
                      _hover={{ bg: 'transparent', color: HelivanColor.text.blueMid }}
                    />
                  </InputRightElement>
                </InputGroup>

                {!isLogin && passwordError && password && (
                  <HStack spacing={2} px={1}>
                    <IoWarningOutline size={14} color="#E53E3E" />
                    <Text fontSize="13px" color="#E53E3E">
                      {passwordError}
                    </Text>
                  </HStack>
                )}

                {!isLogin && (
                  <InputGroup>
                    <InputLeftElement h="48px" pointerEvents="none">
                      <IoLockClosedOutline size={18} color={HelivanColor.text.gray} />
                    </InputLeftElement>
                    <Input
                      {...inputStyles}
                      pl="40px"
                      pr="48px"
                      placeholder="Confirm password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                    />
                    <InputRightElement h="48px">
                      <IconButton
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        icon={showConfirmPassword ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
                        variant="ghost"
                        size="sm"
                        color={HelivanColor.text.gray}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        _hover={{ bg: 'transparent', color: HelivanColor.text.blueMid }}
                      />
                    </InputRightElement>
                  </InputGroup>
                )}

                {passwordMismatchError && (
                  <HStack spacing={2} px={1}>
                    <IoWarningOutline size={14} color="#E53E3E" />
                    <Text fontSize="13px" color="#E53E3E">
                      Passwords do not match
                    </Text>
                  </HStack>
                )}

                <Button
                  type="submit"
                  height="48px"
                  width="full"
                  bg={HelivanColor.text.helivanBlue}
                  color="white"
                  borderRadius="10px"
                  fontSize="15px"
                  fontWeight={500}
                  _hover={{ bg: HelivanColor.text.blueDark }}
                  _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
                  disabled={!!(isLoading || !isFormValid || passwordMismatchError)}
                  transition="all 0.2s"
                >
                  {isLogin ? 'Sign in' : 'Create account'}
                </Button>
              </Stack>

              {error && (
                <Box
                  p={3}
                  bg="red.50"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="red.200"
                >
                  <HStack spacing={2}>
                    <IoWarningOutline size={16} color="#E53E3E" />
                    <Text fontSize="14px" color="red.600">
                      {error}
                    </Text>
                  </HStack>
                </Box>
              )}

              <Text fontSize="14px" color={HelivanColor.text.blueMid} textAlign="center">
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <Link
                  color={HelivanColor.text.helivanBlue}
                  fontWeight={500}
                  onClick={
                    isLoading
                      ? undefined
                      : () => {
                          setIsLogin(!isLogin);
                          setConfirmPassword('');
                          setError('');
                        }
                  }
                  _hover={{ textDecoration: 'underline' }}
                  cursor={isLoading ? 'not-allowed' : 'pointer'}
                >
                  {isLogin ? 'Sign up' : 'Sign in'}
                </Link>
              </Text>
            </>
          ) : (
            <>
              <VStack spacing={2} alignItems="flex-start">
                <Text
                  fontSize="28px"
                  fontWeight={600}
                  color={HelivanColor.text.blueDark}
                >
                  For agents
                </Text>
                <Text
                  fontSize="15px"
                  color={HelivanColor.text.blueMid}
                  lineHeight="1.6"
                >
                  If you're an AI agent, sign yourself up by following the instructions at the link below — no human steps required.
                </Text>
              </VStack>

              {/* Instruction link block with copy */}
              <Box
                bg="#1a1a2e"
                borderRadius="10px"
                p={4}
                position="relative"
                overflow="hidden"
              >
                <IconButton
                  aria-label="Copy instructions link"
                  icon={curlCopied ? <IoCheckmarkOutline size={16} /> : <IoCopyOutline size={16} />}
                  size="sm"
                  position="absolute"
                  top={2}
                  right={2}
                  color={curlCopied ? '#4ade80' : 'gray.400'}
                  bg="transparent"
                  _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                  onClick={() => {
                    navigator.clipboard.writeText('https://agentwatch.helivan.io/skill.md');
                    setCurlCopied(true);
                    setTimeout(() => setCurlCopied(false), 2000);
                  }}
                />
                <Text
                  fontFamily="'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace"
                  fontSize="14px"
                  color="#e2e8f0"
                  lineHeight="1.7"
                  wordBreak="break-all"
                  pr={8}
                >
                  https://agentwatch.helivan.io/skill.md
                </Text>
              </Box>

              <Text fontSize="13px" color={HelivanColor.text.blueMid} lineHeight="1.6">
                Paste that link into your agent, or open it to read the full setup.
              </Text>
            </>
          )}
        </Stack>
      </Flex>
    </Flex>
  );
};

export default Login;
